import { normalizeDrippingDataFromMask } from 'helpers/Dripping';
import { isObject } from 'helpers/Object';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import { action, extendObservable } from 'mobx';
import { ContentPageService } from 'modules/content';
import { DrippingStore } from 'modules/content/dripping/stores';
import drippingPayloadUtil from 'modules/content/dripping/utils/drippingPayloadUtil';
import drippingResponseUtil from 'modules/content/dripping/utils/drippingResponseUtil';
import { FileService } from 'modules/file';
import { UiService } from 'modules/ui';

import AdvertisementDataService from './AdvertisementDataService';
import AdvertisementEditorPreventLeaveService from './AdvertisementEditorPreventLeaveService';
import Store from './AdvertisementEditorStore';

import Request from '@hotmart/request';
class AdvertisementEditorService {
  // #region setup
  @action
  init(data = {}) {
    this.clear();
    this.setData(data);
  }

  @action
  clear() {
    Object.assign(Store, {
      moduleId: null,
      id: null,
      pageId: null,
      published: false,
      name: '',
      marketplaceProductId: '',
      product: {},
      offers: [],
      offer: {},
      content: '',
      playerMedia: null,
      mediaVideoUrl: null,
      media: null,
      image: null,
      dripping: {},
      upsell: [],
      loaded: true,
      affiliateHotlink: null,
      affiliationKey: '',
      snapshot: {}
    });
  }

  @action
  setData(data = {}) {
    extendObservable(Store, data);
  }
  // #endregion

  // #region actions
  @action
  async get(pageId) {
    if (!pageId) {
      return;
    }

    Store.loaded = false;
    let response = null;

    try {
      const medias = await FileService.fetchRecommendationMedia(pageId);
      const images = await FileService.fetchRecommendationImage(pageId);

      response = await Request('recommendation.get', { pageId });
      response.media = medias[0];
      response.image = images[0];

      response = this.filterMediasByPriority(response);
      response.page = normalizeDrippingDataFromMask(response.page);
      response.advertisementData.mediaVideoProvider =
        response.advertisementData.mediaVideoProvider || null;
      response.advertisementData.mediaVideoUrl =
        response.advertisementData.mediaVideoUrl || null;

      const advertisementFormatted = await this.formatReceivedAdvertisement(
        response
      );
      const drippings = drippingResponseUtil.getNormalizedItems(
        advertisementFormatted.drippingConfigs || []
      );

      DrippingStore.setItems(drippings, false);

      // TODO: melhorar a solução do Prevent Leave da página.
      delete advertisementFormatted.drippingConfigs;
      delete advertisementFormatted.classesAvailableForDripping;
      delete response.page.drippingConfigs;
      delete response.page.classesAvailableForDripping;

      this.setData({ ...advertisementFormatted });
      AdvertisementEditorPreventLeaveService.initSnapshot(response);
    } catch (error) {
      this.handleError(error);
    } finally {
      Store.loaded = true;
    }

    return response;
  }

  async getAll() {
    try {
      const response = await Request('recommendation.getAll');

      this.upsell = response;
    } catch (error) {
      this.handleError(error);
    }
  }

  @action
  async getUpsellWidgetData() {
    await this.getAll();
    return this.formatRedactorWidgetData();
  }

  @action
  async create(advertisement) {
    if (
      !advertisement ||
      isEmpty(advertisement) ||
      !ContentPageService.drippingConfigIsValid(advertisement.dripping)
    ) {
      return;
    }

    advertisement = this.formatAdvertisement(advertisement);
    Store.loaded = false;
    let response = null;

    try {
      response = await Request('recommendation.create', advertisement);
      const { id, ...props } = response;

      this.setData({ pageId: id, ...props });
      UiService.setAlertSuccess('done');

      AdvertisementEditorPreventLeaveService.updateSnapshot();
    } catch (error) {
      this.handleError(error);
    } finally {
      Store.loaded = true;
    }

    return response;
  }

  @action
  async update(advertisement) {
    if (
      !advertisement ||
      isEmpty(advertisement) ||
      !ContentPageService.drippingConfigIsValid(advertisement.dripping)
    ) {
      return;
    }

    advertisement = this.formatAdvertisement(advertisement);
    Store.loaded = false;
    let response = null;

    try {
      await Request('recommendation.update', {
        ...advertisement,
        page: {
          ...advertisement?.page,
          ...(Store?.tags && { tags: Store.tags })
        }
      });
      this.setData({ ...response });
      UiService.setAlertSuccess('done');

      AdvertisementEditorPreventLeaveService.updateSnapshot();
      response = true;
    } catch (error) {
      this.handleError(error);
      response = false;
    } finally {
      Store.loaded = true;
    }

    return response;
  }
  // #endregion

  // #region getters & setters
  get upsell() {
    return Store.upsell;
  }

  set upsell(data) {
    Store.upsell = [...data];
  }

  get published() {
    return Store.published;
  }

  set published(status) {
    Store.published = status;
  }

  get pageId() {
    return Store.pageId;
  }

  set moduleId(moduleId) {
    Store.moduleId = moduleId;
  }

  get mediaVideoUrl() {
    return Store.mediaVideoUrl;
  }

  set mediaVideoUrl(value) {
    Store.mediaVideoUrl = value;
  }
  // #endregion

  // #region helpers
  formatAdvertisement(advertisement) {
    const { pageId } = Store;

    const advertisementFormatted = {
      advertisementData: AdvertisementDataService.getData(advertisement),
      page: {
        ...this.advertisementPage(advertisement),
        ...drippingPayloadUtil.getPayload(!pageId)
      }
    };

    if (pageId) {
      advertisementFormatted.id = pageId;
    }

    return advertisementFormatted;
  }

  advertisementPage(advertisement) {
    const { pageId, moduleId, published, language, signLanguage } = Store;
    const { name, content, dripping } = advertisement;
    const { durationDays, liberationDays, ...drippingProps } = dripping;

    const pageLanguage = {
      language,
      signLanguage: Boolean(signLanguage)
    };

    const advertisementPage = Object.assign(
      {
        hasDuration: null,
        durationDays: null,
        liberationDate: null,
        liberationDays: null,
        liberationType: null,
        type: 'ADVERTISEMENT'
      },
      {
        content,
        moduleId,
        name,
        published,
        durationDays,
        liberationDays,
        pageLanguage: pageLanguage.language ? pageLanguage : null,
        ...drippingProps
      }
    );

    if (pageId) {
      advertisementPage.id = pageId;
    }

    return advertisementPage;
  }

  formatReceivedAdvertisement(ad) {
    const { advertisementData } = ad;
    let { page } = ad;

    const {
      id,
      hasDuration,
      durationDays,
      liberationDate,
      liberationDays,
      liberationType,
      pageLanguage,
      ...pageProps
    } = page;

    page = {
      pageId: id,
      ...pageProps,
      dripping: {
        hasDuration,
        durationDays,
        liberationDate,
        liberationDays,
        liberationType
      },
      language: pageLanguage?.language || '',
      signLanguage: pageLanguage?.signLanguage || false
    };

    return {
      ...advertisementData,
      ...page,
      media: ad.media || ad.image
    };
  }

  filterMediasByPriority(response) {
    if (!isEmpty(response.media)) {
      response.advertisementData.mediaVideoUrl = void 0;
      response.advertisementData.mediaVideoProvider = void 0;
      response.image = void 0;
    } else if (
      response.advertisementData &&
      !isEmpty(response.advertisementData.mediaVideoUrl)
    ) {
      response.image = void 0;
    }

    return response;
  }

  formatRedactorWidgetData() {
    return groupBy(this.upsell, 'moduleName');
  }

  handleError(error = {}) {
    const { data } = error;
    const invalidHashError =
      isObject(data) && data.errorCode && data.errorCode === '0007';

    if (invalidHashError) {
      window.h.replace('new', { pageId: 'new' });
    }

    UiService.handleError(error);
  }
  // #endregion
}

export default new AdvertisementEditorService();
